/**
 * Quote PDF fix run (2026-10-27, A1) — the customer-facing product names and
 * descriptions, by internal product key.
 *
 * The internal keys (`eh`, `ih`, `sp`, `ta`) never change. What a customer
 * reads does: the four retired names (External Hiring, Internal Hiring,
 * Succession Planning, Team Assessments) are replaced by the Blueprint names.
 *
 * Where a name comes from, in order:
 *   1. the current pricing config's `displayName` / `description` (editable on
 *      /admin/pricing), unless it still carries a retired name;
 *   2. the canonical copy below.
 * The name frozen into a saved quote line (`productName`) is NOT used on
 * customer documents, so a quote saved before the rename still renders with
 * the current name. Its key decides the name.
 */
export const PRODUCT_CATALOG_COPY = {
    eh: {
        displayName: 'Role Blueprint™ (external hire)',
        description: 'One candidate pool assessed against one role.',
    },
    ih: {
        displayName: 'Role Blueprint™ (internal move)',
        description: 'Up to 6 internal candidates assessed against one role; $200 for each candidate beyond 6.',
    },
    sp: {
        displayName: 'Succession Blueprint',
        description: 'One role plus up to 3 candidates, cascading one layer: up to 12 candidates across the cascade.',
    },
    ta: {
        displayName: 'Team Blueprint',
        description: 'Team mission assessment plus an individual page for every member. Minimum 3 members.',
    },
};
/** The indented second line of a Team Blueprint order (A2). */
export const TEAM_MEMBER_LINE_LABEL = 'Team members assessed';
/**
 * Names that must never reach a customer document. Matched case-insensitively
 * as whole phrases, so "team assessment" inside a longer sentence is caught
 * too. Lowercase descriptive use is fine in the CRM, not on a quote.
 */
export const RETIRED_PRODUCT_NAMES = [
    'External Hiring',
    'Internal Hiring',
    'Succession Planning',
    'Team Assessments',
    'Team Assessment',
];
export function findRetiredProductName(text) {
    const lower = text.toLowerCase();
    for (const name of RETIRED_PRODUCT_NAMES) {
        if (lower.includes(name.toLowerCase()))
            return name;
    }
    return null;
}
function usable(value) {
    return typeof value === 'string' && value.trim() !== '' && findRetiredProductName(value) === null;
}
function isCatalogKey(key) {
    return key === 'eh' || key === 'ih' || key === 'sp' || key === 'ta';
}
/**
 * The name and description a customer document shows for one product key.
 * An unknown key keeps the caller's fallback name (never a retired one) and
 * no description.
 */
export function customerProductCopy(productKey, catalog, fallbackName) {
    const entry = catalog?.[productKey];
    if (isCatalogKey(productKey)) {
        const canonical = PRODUCT_CATALOG_COPY[productKey];
        return {
            displayName: usable(entry?.displayName) ? entry.displayName.trim() : canonical.displayName,
            description: usable(entry?.description) ? entry.description.trim() : canonical.description,
        };
    }
    return {
        displayName: usable(fallbackName) ? fallbackName.trim() : 'Assessment',
        description: '',
    };
}
//# sourceMappingURL=catalog-copy.js.map